import type Project from "@/types/Project";
import mark from "./antimanti-tile.webp";

const media = "/case_studies/antimanti";

export const antimanti: Project = {
    name: "Antimanti",
    logo: mark,
    colors: ["#3265ec", "#9ab8ff"],
    description: "A Persian storefront for antivirus licenses, with device-first discovery, partner pricing, and digital fulfillment after payment.",
    skills: ["Frontend Architecture", "Backend Engineering", "DevOps & Data Layer"],
    tech_stack_ids: ["nextjs", "react", "typescript", "tailwind", "postgresql", "prisma", "rest-api", "docker", "i18n"],
    started_date: "Nov 2025",
    accessibility: "public",
    url: "https://www.antimanti.ir/",
    highlights: [
        "Persian storefront with filters for operating system, device count, and availability",
        "Quantity-based pricing for partner accounts",
        "Zarinpal payment verification and transactional license allocation",
    ],
    case_study: {
        slug: "antimanti",
        project_name: "Antimanti",
        published_at: "2026-11-03T00:00:00.000Z",
        thumbnail_image: mark,
        hero_image: media + "/hero.webp",
        hero_image_alt: "A sculptural white ceramic shield with a cobalt glass core",
        hero_eyebrow: "Antimanti / Persian commerce",
        hero_title: "From checkout to activation.",
        hero_subtitle: "Antimanti makes it easier to choose and buy antivirus protection in Persian. Behind the storefront, a verified payment starts the work of delivering the right license.",
        overview: {
            role: "Full-Stack Development",
            platforms: ["Responsive Web"],
            summary: "Antimanti brings antivirus discovery, checkout, and license delivery into one Persian shopping experience. Shoppers start with their device and compare compatible products. Partners can buy in volume, and operators can manage the stock of individual activation keys.",
        },
        gallery: [
            {
                type: "image",
                src: media + "/storefront.webp",
                width: 2460,
                height: 1536,
                alt: "Persian Antimanti storefront showing its cobalt shield, product search, and operating system shortcuts",
                caption: "<strong>01 / Start with the device.</strong> Operating system shortcuts give shoppers a way into the catalog before they know which antivirus brand they want.",
            },
            {
                type: "image",
                src: media + "/catalog.webp",
                width: 2460,
                height: 1800,
                alt: "Persian Antimanti catalog showing antivirus product cards and filters for compatibility and availability",
                caption: "<strong>02 / Compare the details that matter.</strong> Compatibility, device count, price, and availability help shoppers narrow the catalog and make a choice.",
            },
        ],
        challenge: {
            title: "A digital purchase has two finish lines",
            description: "The storefront has to help customers choose protection that works on their devices. Once they pay, the system has to allocate an actual activation key from a finite pool.",
            points: [
                "Mixed Persian and Latin product names, local prices, and right-to-left reading order shape the interface.",
                "Retail customers and volume buyers share a catalog, while quantity and partner status can change the unit price.",
                "Two orders can compete for the same license. Allocation needs to handle that collision without assigning a key twice.",
            ],
        },
        solution: {
            title: "Follow the license",
            description: "Three decisions connect the visible shopping experience to the systems that price and fulfill an order.",
            approaches: [
                {
                    title: "01 / Put compatibility up front",
                    description: "The Persian storefront starts with device and operating system shortcuts. The catalog then gives shoppers filters for operating system, category, device count, and availability.",
                    techniques: ["Right-to-left layouts and Persian typography", "Search and structured filters for the same catalog"],
                },
                {
                    title: "02 / Price for the buyer",
                    description: "Products can define quantity bands for partner accounts. The server resolves the applicable tier from the cart quantity so the price can be calculated as part of checkout.",
                    techniques: ["Product-level quantity tiers", "Partner pricing calculated on the server"],
                },
                {
                    title: "03 / Verify, then allocate",
                    description: "The payment service verifies the transaction with Zarinpal before starting license delivery. Fulfillment claims keys inside a database transaction and checks that each key is still available at the moment of assignment.",
                    techniques: ["Gateway verification precedes delivery", "Conditional updates guard against concurrent claims"],
                },
            ],
        },
        tech_details: {
            title: "The systems behind the storefront",
            description: "Next.js serves the store, customer accounts, and admin workspace. Services handle business rules, with Prisma and PostgreSQL storing orders, payments, products, and individual licenses.",
            architecture_highlights: [
                "Phone authentication uses an SMS code and an HTTP-only session cookie.",
                "The admin workspace supports bulk license imports alongside order and inventory management.",
                "Payment verification and license delivery are separate steps, so a verified payment and a delivery failure can be recorded independently.",
            ],
        },
        conclusion: {
            title: "The purchase ends at the activation key",
            content: "Antimanti ties three distinct tasks together. The shopper finds compatible protection, the server verifies payment, and the fulfillment service assigns a key. Keeping those responsibilities distinct makes the system easier to reason about when orders compete for stock or delivery fails.",
            key_takeaways: [
                "Put device compatibility where shoppers make their first decision.",
                "Treat each activation key as an inventory item with its own state.",
                "Model payment verification and fulfillment as separate outcomes.",
            ],
        },
    },
};
